import { useMemo } from 'react';
import {
  getChartIntervalStartDate,
  getChartIntervalEndDate,
  getWeeklyPeriodString,
} from '../../components/ChartTypeSwitcher';
import type { BarChartType } from '../../components/BarChart/types';
import type { IAnalyticsChartDataPoint } from '../../redux/types';

export type ChartDataPoint = { x: string; y: number };

const DAILY_DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKLY_LABELS = ['Week 1', 'Week 2', 'Week 3', 'Week 4'] as const;
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function toYYYYMMDD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dailyApiDataToChartData(
  points: IAnalyticsChartDataPoint[],
  periodOffset: number
): ChartDataPoint[] {
  const valueByDate = new Map(points.map(({ period, value }) => [period, value]));
  const weekStart = getChartIntervalStartDate('daily', periodOffset);
  return DAILY_DAY_NAMES.map((dayName, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const period = toYYYYMMDD(d);
    return { x: dayName, y: valueByDate.get(period) ?? 0 };
  });
}

function weeklyApiDataToChartData(
  points: IAnalyticsChartDataPoint[],
  periodOffset: number
): ChartDataPoint[] {
  const valueByPeriod = new Map(points.map(({ period, value }) => [period, value]));
  return WEEKLY_LABELS.map((label, i) => {
    const weekOffset = periodOffset - (3 - i);
    const period = getWeeklyPeriodString(weekOffset);
    return { x: label, y: valueByPeriod.get(period) ?? 0 };
  });
}

function monthlyApiDataToChartData(
  points: IAnalyticsChartDataPoint[],
  periodOffset: number
): ChartDataPoint[] {
  const valueByPeriod = new Map(points.map(({ period, value }) => [period, value]));
  const end = getChartIntervalEndDate('monthly', periodOffset);
  const year = end.getFullYear();
  return MONTH_LABELS.map((label, i) => {
    const period = `${year}-${String(i + 1).padStart(2, '0')}`;
    return { x: label, y: valueByPeriod.get(period) ?? 0 };
  });
}

function apiDataToChartData(
  points: IAnalyticsChartDataPoint[],
  chartType: BarChartType,
  periodOffset: number
): ChartDataPoint[] {
  if (chartType === 'daily') {
    return dailyApiDataToChartData(points, periodOffset);
  }
  if (chartType === 'weekly') {
    return weeklyApiDataToChartData(points, periodOffset);
  }
  if (chartType === 'monthly') {
    return monthlyApiDataToChartData(points, periodOffset);
  }
  return points.length > 0 ? points.map(({ period, value }) => ({ x: period, y: value })) : [];
}

/**
 * Transforms analytics API chart points into chart-ready data by chart type.
 * Daily: 7 bars (Mon–Sun) with day names; weekly: 4 bars (Week 1–4); monthly: 12 bars (Jan–Dec) with month names.
 */
function useChartData(
  points: IAnalyticsChartDataPoint[] | undefined,
  chartType: BarChartType,
  periodOffset: number
): ChartDataPoint[] {
  return useMemo(
    () => apiDataToChartData(points ?? [], chartType, periodOffset),
    [points, chartType, periodOffset]
  );
}

export default useChartData;
